import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Button, Descriptions, Empty, Input, InputNumber, Modal, Popconfirm, Progress, Segmented, Select, Space, Spin, Switch, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { useTranslation } from '../i18n';
import { isOk, errMsg, arr } from '../services/dedicatedService';

const { Text } = Typography;
const mono = { fontFamily: 'monospace' };
const when = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '-');

/** Run a tool call; toast the error, return data (or null). */
const useCall = () => {
    const { t } = useTranslation();
    return useCallback(async (p, ok) => {
        const r = await p;
        if (!isOk(r)) { message.error(errMsg(r, t('Operation failed'))); return null; }
        if (ok) message.success(ok);
        return r.data || {};
    }, [t]);
};

const Bandwidth = ({ tools, usage }) => {
    const { t } = useTranslation();
    const [period, setPeriod] = useState('day');
    const [d, setD] = useState(null);
    useEffect(() => {
        setD(null);
        tools.bandwidth(period).then((r) => setD(isOk(r) ? r.data : { points: [] }));
    }, [tools, period]);
    const vals = arr(d?.points).map((p) => Number(p.in || 0) + Number(p.out || 0));
    const max = Math.max(1, ...vals);
    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            {usage?.allowed != null && <Text>{t('Used {{used}} of {{allowed}}', { used: usage.used ?? 0, allowed: usage.allowed })}</Text>}
            <Segmented value={period} onChange={setPeriod}
                options={[['day', 'Day'], ['week', 'Week'], ['month', 'Month'], ['year', 'Year']].map(([v, l]) => ({ value: v, label: t(l) }))} />
            {d === null ? <Spin /> : vals.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
                <>
                    <svg viewBox={`0 0 ${vals.length} 100`} preserveAspectRatio="none" style={{ width: '100%', height: 120 }}>
                        <polyline fill="none" stroke="currentColor" strokeWidth="0.6" vectorEffect="non-scaling-stroke"
                            points={vals.map((v, i) => `${i},${100 - (v / max) * 95}`).join(' ')} />
                    </svg>
                    <Text type="secondary">{t('Peak')}: {max.toFixed(1)} {d.unit}</Text>
                </>
            )}
        </Space>
    );
};

const Rdns = ({ tools, ips }) => {
    const { t } = useTranslation();
    const call = useCall();
    const [edit, setEdit] = useState(null);
    const [value, setValue] = useState('');
    const open = async (ip) => {
        setEdit(ip); setValue('');
        const d = await tools.rdnsGet({ ip_address_id: ip.id, ip: ip.ip });
        if (isOk(d)) setValue(d.data?.rdns || '');
    };
    const save = async () => {
        if (await call(tools.rdnsSet({ ip_address_id: edit.id, ip: edit.ip, rdns: value.trim() }), t('Saved'))) setEdit(null);
    };
    return (
        <>
            <Table size="small" rowKey="ip" pagination={false} dataSource={arr(ips)} scroll={{ x: 'max-content' }}
                columns={[
                    { title: t('IP address'), dataIndex: 'ip', render: (v) => <Text copyable style={mono}>{v}</Text> },
                    { title: t('Gateway'), dataIndex: 'gateway' },
                    { title: t('Subnet'), dataIndex: 'subnet' },
                    { title: '', key: 'op', width: 120, render: (_, ip) => <Button size="small" onClick={() => open(ip)}>{t('Set rDNS')}</Button> },
                ]} />
            <Modal open={!!edit} title={`rDNS · ${edit?.ip || ''}`} onCancel={() => setEdit(null)} onOk={save} okButtonProps={{ disabled: !value.trim() }}>
                <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="mail.example.com" />
            </Modal>
        </>
    );
};

const Reinstall = ({ tools, canCancel, onChanged }) => {
    const { t } = useTranslation();
    const call = useCall();
    const [os, setOs] = useState(null);
    const [osId, setOsId] = useState();
    const [schemes, setSchemes] = useState([]);
    const [scheme, setScheme] = useState();
    const [license, setLicense] = useState('');
    const [st, setSt] = useState(null);
    const [busy, setBusy] = useState('');

    const status = useCallback(async () => {
        const r = await tools.reinstallStatus();
        if (isOk(r)) setSt(r.data);
    }, [tools]);
    useEffect(() => {
        tools.osList().then((r) => setOs(isOk(r) ? arr(r.data?.list) : []));
        status();
    }, [tools, status]);
    useEffect(() => {
        if (st?.state !== 'running') return undefined;
        const h = setTimeout(status, 15000);
        return () => clearTimeout(h);
    }, [st, status]);
    useEffect(() => {
        setScheme(undefined); setSchemes([]);
        if (!osId) return;
        tools.partitioning(osId).then((r) => {
            const l = isOk(r) ? arr(r.data?.list) : [];
            setSchemes(l);
            if (l[0]) setScheme(l[0].id);
        });
    }, [tools, osId]);

    const start = async () => {
        setBusy('start');
        const d = await call(tools.reinstall({ os_id: osId, partitioning_id: scheme, license_key: license.trim() }), t('Reinstall started'));
        setBusy('');
        if (d) { setSt(d); onChanged?.(); }
    };
    const cancel = async () => {
        setBusy('cancel');
        const d = await call(tools.reinstallCancel(), t('Done'));
        setBusy('');
        if (d) setSt(d);
    };

    if (os === null) return <Spin />;
    const name = arr(os).find((o) => o.id === osId)?.name || '';
    const running = st?.state === 'running';
    return (
        <Space direction="vertical" style={{ width: '100%', maxWidth: 520 }}>
            {running && (
                <Alert type="info" showIcon message={st.text || t('Reinstalling')}
                    description={st.progress != null && <Progress percent={st.progress} size="small" />}
                    action={canCancel && <Popconfirm title={t('Cancel the reinstall?')} onConfirm={cancel}><Button size="small" loading={busy === 'cancel'}>{t('Cancel')}</Button></Popconfirm>} />
            )}
            {st?.state === 'failed' && <Alert type="error" showIcon message={st.text || t('Reinstall failed')} />}
            {st?.state === 'done' && st.text && <Alert type="success" showIcon message={st.text} />}
            <Select placeholder={t('Operating system')} value={osId} onChange={setOsId} showSearch optionFilterProp="label" disabled={running}
                options={arr(os).map((o) => ({ value: o.id, label: o.name }))} />
            {schemes.length > 0 && <Select value={scheme} onChange={setScheme} disabled={running} options={schemes.map((s) => ({ value: s.id, label: s.name }))} />}
            {/windows/i.test(name) && <Input value={license} onChange={(e) => setLicense(e.target.value)} placeholder={t('License key (optional)')} />}
            <Popconfirm title={t('All data on the server will be erased. Continue?')} okButtonProps={{ danger: true }} onConfirm={start} disabled={!osId || running}>
                <Button danger disabled={!osId || running} loading={busy === 'start'}>{t('Reinstall')}</Button>
            </Popconfirm>
        </Space>
    );
};

/** Rescue boot (no KVM on these): pick an image → the server reboots into it; the login shows up once it is up. */
const Rescue = ({ tools }) => {
    const { t } = useTranslation();
    const call = useCall();
    const [d, setD] = useState(null);
    const [img, setImg] = useState();
    const [busy, setBusy] = useState('');
    const load = useCallback(async () => {
        const r = await tools.rescueStatus();
        setD(isOk(r) ? r.data : {});
    }, [tools]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (d?.mode !== 'rescue' || d?.credentials) return undefined;
        const h = setTimeout(load, 20000);
        return () => clearTimeout(h);
    }, [d, load]);
    const run = async (image) => {
        setBusy(image ? 'on' : 'off');
        const r = await call(tools.rescue({ image }), t('Rebooting'));
        setBusy('');
        if (r) setD(r);
    };

    if (!d) return <Spin />;
    const c = d.credentials;
    if (d.mode === 'rescue') {
        return (
            <Space direction="vertical" style={{ width: '100%', maxWidth: 520 }}>
                <Alert type="warning" showIcon message={t('Booted into the rescue system')} />
                {c ? (
                    <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label={t('Login')}><Text copyable style={mono}>{c.login}</Text>{c.protocol && <Tag style={{ marginLeft: 8 }}>{c.protocol}</Tag>}</Descriptions.Item>
                        <Descriptions.Item label={t('Password')}><Text copyable style={mono}>{c.password}</Text></Descriptions.Item>
                        {c.ip && <Descriptions.Item label={t('IP address')}><Text copyable style={mono}>{c.ip}</Text></Descriptions.Item>}
                    </Descriptions>
                ) : <Text type="secondary"><Spin size="small" /> {t('Starting…')}</Text>}
                <Popconfirm title={t('Reboot into the installed system?')} onConfirm={() => run('')}>
                    <Button loading={busy === 'off'}>{t('Exit rescue mode')}</Button>
                </Popconfirm>
            </Space>
        );
    }
    return (
        <Space direction="vertical" style={{ width: '100%', maxWidth: 520 }}>
            <Select placeholder={t('Rescue image')} value={img} onChange={setImg} options={arr(d.images).map((i) => ({ value: i, label: i }))} />
            <Popconfirm title={t('The server will reboot into the rescue system. Continue?')} onConfirm={() => run(img)} disabled={!img}>
                <Button disabled={!img} loading={busy === 'on'}>{t('Boot into rescue')}</Button>
            </Popconfirm>
        </Space>
    );
};

const NullRoutes = ({ tools, ips }) => {
    const { t } = useTranslation();
    const call = useCall();
    const [list, setList] = useState(null);
    const [ip, setIp] = useState();
    const [hours, setHours] = useState(24);
    const [busy, setBusy] = useState('');
    useEffect(() => { tools.nullRoutes().then((r) => setList(isOk(r) ? arr(r.data?.list) : [])); }, [tools]);
    const run = async (key, p, ok) => {
        setBusy(key);
        const d = await call(p, ok);
        setBusy('');
        if (d) setList(arr(d.list));
    };
    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Space wrap>
                <Select style={{ minWidth: 200 }} placeholder={t('IP address')} value={ip} onChange={setIp} options={arr(ips).map((x) => ({ value: x.ip, label: x.ip }))} />
                <InputNumber min={1} max={720} value={hours} onChange={setHours} addonAfter={t('hours')} />
                <Popconfirm title={t('All traffic to this IP will be dropped. Continue?')} okButtonProps={{ danger: true }} disabled={!ip}
                    onConfirm={() => run('add', tools.nullRouteAdd({ ip, hours }), t('Done'))}>
                    <Button danger disabled={!ip} loading={busy === 'add'}>{t('Add null route')}</Button>
                </Popconfirm>
            </Space>
            <Table size="small" rowKey={(r) => `${r.ip}-${r.id}`} loading={list === null} dataSource={arr(list)} pagination={false} scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No null routes')} /> }}
                columns={[
                    { title: t('IP address'), dataIndex: 'ip', render: (v) => <Text style={mono}>{v}</Text> },
                    { title: t('Reason'), dataIndex: 'reason', render: (v) => v || '-' },
                    { title: t('Created'), dataIndex: 'created_at', render: when },
                    { title: t('Expires'), dataIndex: 'expires_at', render: when },
                    {
                        title: '', key: 'op', width: 100,
                        render: (_, r) => (r.removable
                            ? <Button size="small" loading={busy === r.id} onClick={() => run(r.id, tools.nullRouteRemove({ ip: r.ip, route_id: r.id }), t('Removed'))}>{t('Remove')}</Button>
                            : <Tag>{t('Locked')}</Tag>),
                    },
                ]} />
        </Space>
    );
};

const Ddos = ({ tools }) => {
    const { t } = useTranslation();
    const [list, setList] = useState(null);
    useEffect(() => { tools.ddos().then((r) => setList(isOk(r) ? arr(r.data?.list) : [])); }, [tools]);
    return (
        <Table size="small" rowKey={(r) => `${r.ip}-${r.id}`} loading={list === null} dataSource={arr(list)} pagination={{ pageSize: 20, hideOnSinglePage: true }}
            scroll={{ x: 'max-content' }} locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No attacks recorded')} /> }}
            columns={[
                { title: t('IP address'), dataIndex: 'ip', render: (v) => <Text style={mono}>{v}</Text> },
                { title: t('Type'), dataIndex: 'type', render: (v) => v || '-' },
                { title: t('Peak'), dataIndex: 'peak', render: (v) => v || '-' },
                { title: t('Started'), dataIndex: 'started_at', render: when },
                { title: t('Ended'), dataIndex: 'ended_at', render: (v) => (v ? when(v) : <Tag color="red">{t('Ongoing')}</Tag>) },
            ]} />
    );
};

const Backup = ({ tools }) => {
    const { t } = useTranslation();
    const call = useCall();
    const [list, setList] = useState(null);
    const [pw, setPw] = useState(null); // { account, value }
    const [busy, setBusy] = useState('');
    useEffect(() => { tools.backup().then((r) => setList(isOk(r) ? arr(r.data?.list) : [])); }, [tools]);
    const save = async (a, enable, password) => {
        setBusy(String(a.id));
        const d = await call(tools.backupAccount({ account_id: a.id, enable, password: password || '' }), t('Saved'));
        setBusy('');
        if (d) { setList(arr(d.list)); setPw(null); }
    };
    if (list === null) return <Spin />;
    if (!list.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No backup space on this server')} />;
    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            {list.map((s) => (
                <div key={s.id}>
                    <Space wrap style={{ marginBottom: 8 }}>
                        <Text strong>{s.name}</Text>
                        {s.host && <Text copyable style={mono}>{s.host}</Text>}
                        {(s.used || s.size) && <Text type="secondary">{[s.used, s.size].filter(Boolean).join(' / ')}</Text>}
                    </Space>
                    <Table size="small" rowKey="id" pagination={false} dataSource={arr(s.accounts)}
                        columns={[
                            { title: t('Username'), dataIndex: 'username', render: (v) => <Text copyable style={mono}>{v}</Text> },
                            { title: t('Enabled'), dataIndex: 'enabled', width: 90, render: (v, a) => <Switch size="small" checked={v} loading={busy === String(a.id)} onChange={(on) => save(a, on)} /> },
                            { title: '', key: 'pw', width: 140, render: (_, a) => <Button size="small" onClick={() => setPw({ account: a, value: '' })}>{t('Set password')}</Button> },
                        ]} />
                </div>
            ))}
            <Modal open={!!pw} title={pw?.account?.username} onCancel={() => setPw(null)} confirmLoading={!!busy}
                onOk={() => save(pw.account, true, pw.value)} okButtonProps={{ disabled: !pw || pw.value.length < 8 || pw.value.length > 64 }}>
                <Input.Password value={pw?.value} onChange={(e) => setPw({ ...pw, value: e.target.value })} placeholder={t('8 to 64 characters')} />
            </Modal>
        </Space>
    );
};

/**
 * Management tabs for one dedicated server, shared by the customer page and the admin drawer.
 * `tools` = userTools(id) | adminTools(id) (memoise it); `caps` = the supplier's capabilities — missing ones are hidden.
 */
const DedicatedServerTools = ({ tools, caps, ips, usage, onChanged }) => {
    const { t } = useTranslation();
    const has = (c) => arr(caps).includes(c);
    const items = [
        has('bandwidth') && { key: 'bw', label: t('Bandwidth'), children: <Bandwidth tools={tools} usage={usage} /> },
        has('rdns') && { key: 'rdns', label: 'rDNS', children: <Rdns tools={tools} ips={ips} /> },
        has('reinstall') && { key: 'os', label: t('Reinstall'), children: <Reinstall tools={tools} canCancel={has('reinstall_cancel')} onChanged={onChanged} /> },
        has('rescue') && { key: 'rescue', label: t('Rescue mode'), children: <Rescue tools={tools} /> },
        has('null_routes') && { key: 'nr', label: t('Null routes'), children: <NullRoutes tools={tools} ips={ips} /> },
        has('ddos') && { key: 'ddos', label: t('DDoS attacks'), children: <Ddos tools={tools} /> },
        has('backup') && { key: 'backup', label: t('Backup space'), children: <Backup tools={tools} /> },
    ].filter(Boolean);
    if (!items.length) return null;
    return <Tabs destroyInactiveTabPane items={items} />;
};

export default DedicatedServerTools;
